import { z } from 'zod';
import {
  AccountSchema,
  AdSchema,
  AssetGroupSchema,
  AssetSchema,
  AudienceSchema,
  AuditSummarySchema,
  BiddingStrategySchema,
  BudgetSchema,
  CampaignSchema,
  ChangeHistorySchema,
  ConversionActionSchema,
  ConversionStatsSchema,
  DescriptionSchema,
  ExportProgressSchema,
  ExportWrapperSchema,
  ExtensionSchema,
  GoogleAdsConnectionSchema,
  HeadlineSchema,
  ImageAssetSchema,
  KeywordSchema,
  LabelSchema,
  LandingPageSchema,
  LocationTargetSchema,
  NegativeKeywordSchema,
  PerformanceSchema,
  QualitySignalsSchema,
  RecommendationImpactSchema,
  RecommendationSchema,
  SearchTermSchema,
  ShoppingSchema,
  VideoAssetSchema,
} from './schemas';

// Re-export schemas
export * from './schemas';

// Inferred TypeScript types from Zod schemas
export type Account = z.infer<typeof AccountSchema>;
export type Campaign = z.infer<typeof CampaignSchema>;
export type AssetGroup = z.infer<typeof AssetGroupSchema>;
export type Asset = z.infer<typeof AssetSchema>;
export type Ad = z.infer<typeof AdSchema>;
export type Keyword = z.infer<typeof KeywordSchema>;
export type NegativeKeyword = z.infer<typeof NegativeKeywordSchema>;
export type SearchTerm = z.infer<typeof SearchTermSchema>;
export type LocationTarget = z.infer<typeof LocationTargetSchema>;
export type Audience = z.infer<typeof AudienceSchema>;
export type Recommendation = z.infer<typeof RecommendationSchema>;
export type ConversionAction = z.infer<typeof ConversionActionSchema>;
export type ConversionStats = z.infer<typeof ConversionStatsSchema>;
export type Budget = z.infer<typeof BudgetSchema>;
export type BiddingStrategy = z.infer<typeof BiddingStrategySchema>;
export type Extension = z.infer<typeof ExtensionSchema>;
export type LandingPage = z.infer<typeof LandingPageSchema>;
export type Performance = z.infer<typeof PerformanceSchema>;
export type QualitySignals = z.infer<typeof QualitySignalsSchema>;
export type Label = z.infer<typeof LabelSchema>;
export type Shopping = z.infer<typeof ShoppingSchema>;
export type ChangeHistory = z.infer<typeof ChangeHistorySchema>;
export type AuditSummary = z.infer<typeof AuditSummarySchema>;
export type Headline = z.infer<typeof HeadlineSchema>;
export type Description = z.infer<typeof DescriptionSchema>;
export type ImageAsset = z.infer<typeof ImageAssetSchema>;
export type VideoAsset = z.infer<typeof VideoAssetSchema>;
export type RecommendationImpact = z.infer<typeof RecommendationImpactSchema>;
export type GoogleAdsConnection = z.infer<typeof GoogleAdsConnectionSchema>;
export type ExportProgress = z.infer<typeof ExportProgressSchema>;

// Wrapper types for exports
export type ExportWrapper<T> = {
  generatedAt: string;
  customerId: string;
  apiVersion: string;
  data: T[];
};

export type AccountExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof AccountSchema>>>;
export type CampaignExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof CampaignSchema>>>;
export type AssetGroupExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof AssetGroupSchema>>>;
export type AssetExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof AssetSchema>>>;
export type AdExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof AdSchema>>>;
export type KeywordExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof KeywordSchema>>>;
export type NegativeKeywordExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof NegativeKeywordSchema>>>;
export type SearchTermExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof SearchTermSchema>>>;
export type LocationTargetExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof LocationTargetSchema>>>;
export type AudienceExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof AudienceSchema>>>;
export type RecommendationExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof RecommendationSchema>>>;
export type ConversionActionExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof ConversionActionSchema>>>;
export type ConversionStatsExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof ConversionStatsSchema>>>;
export type BudgetExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof BudgetSchema>>>;
export type BiddingStrategyExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof BiddingStrategySchema>>>;
export type ExtensionExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof ExtensionSchema>>>;
export type LandingPageExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof LandingPageSchema>>>;
export type PerformanceExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof PerformanceSchema>>>;
export type QualitySignalsExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof QualitySignalsSchema>>>;
export type LabelExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof LabelSchema>>>;
export type ShoppingExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof ShoppingSchema>>>;
export type ChangeHistoryExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof ChangeHistorySchema>>>;
export type HeadlineExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof HeadlineSchema>>>;
export type DescriptionExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof DescriptionSchema>>>;
export type ImageAssetExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof ImageAssetSchema>>>;
export type VideoAssetExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof VideoAssetSchema>>>;
export type RecommendationImpactExport = z.infer<ReturnType<typeof ExportWrapperSchema<typeof RecommendationImpactSchema>>>;

// Date range options for exports
export type DateRangeOption = 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'LAST_90_DAYS' | 'LAST_365_DAYS' | 'CUSTOM';

export interface ExportOptions {
  customerId: string;
  dateRange: DateRangeOption;
  customStartDate?: string;
  customEndDate?: string;
  includeChangeHistory?: boolean;
  changeHistoryMonths?: number;
}

// Pre-signed S3 download returned by the export endpoints
export interface ExportDownload {
  downloadUrl: string;
  fileName: string;
  sizeBytes?: number;
  expiresIn: number;
  errors?: string[];
}

// Full audit data structure
export interface GoogleAdsAuditData {
  account: Account;
  campaigns: Campaign[];
  assetGroups: AssetGroup[];
  ads: Ad[];
  assets: Asset[];
  headlines: Headline[];
  descriptions: Description[];
  images: ImageAsset[];
  videos: VideoAsset[];
  keywords: Keyword[];
  negativeKeywords: NegativeKeyword[];
  searchTerms: SearchTerm[];
  locations: LocationTarget[];
  audiences: Audience[];
  recommendations: Recommendation[];
  recommendationImpact: RecommendationImpact[];
  conversionActions: ConversionAction[];
  conversionStats: ConversionStats[];
  budgets: Budget[];
  biddingStrategies: BiddingStrategy[];
  extensions: Extension[];
  landingPages: LandingPage[];
  performance: Performance[];
  qualitySignals: QualitySignals[];
  labels: Label[];
  shopping: Shopping[];
  changeHistory: ChangeHistory[];
  summary: AuditSummary;
}

// Helper to format micros to currency
export function microsToAmount(micros: number): number {
  return micros / 1_000_000;
}

// Helper to format percentage
export function formatPercentage(value: number | null): string {
  if (value === null) return '-';
  return `${(value * 100).toFixed(2)}%`;
}

// Helper to get status color
export function getStatusColor(status: string): string {
  switch (status) {
    case 'ENABLED':
      return 'green';
    case 'PAUSED':
      return 'yellow';
    case 'REMOVED':
    case 'CANCELED':
    case 'SUSPENDED':
    case 'CLOSED':
      return 'red';
    default:
      return 'gray';
  }
}

// Helper to get quality score color
export function getQualityScoreColor(score: number | null): string {
  if (score === null) return 'gray';
  if (score >= 7) return 'green';
  if (score >= 4) return 'yellow';
  return 'red';
}

// Helper to get ad strength color
export function getAdStrengthColor(strength: string | null): string {
  switch (strength) {
    case 'EXCELLENT':
      return 'green';
    case 'GOOD':
      return 'teal';
    case 'AVERAGE':
      return 'yellow';
    case 'POOR':
      return 'red';
    default:
      return 'gray';
  }
}

// Helper to get performance label color
export function getPerformanceLabelColor(label: string | null): string {
  switch (label) {
    case 'BEST':
      return 'green';
    case 'GOOD':
      return 'teal';
    case 'LOW':
      return 'red';
    case 'LEARNING':
      return 'blue';
    case 'PENDING':
      return 'gray';
    default:
      return 'gray';
  }
}
